import React, { useEffect, useState } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { 
  LayoutDashboard,
  BookOpen,
  FileText, 
  Users, 
  LogOut, 
  Home, 
  Brain, 
  Plug, 
  Calendar, 
  BarChart3, 
  Cpu,
  ChevronDown,
  ChevronRight,
  Settings,
  Tags,
  MessageSquare,
  Shield,
  Mail,
  User,
  GraduationCap
} from "lucide-react";
import DashboardHeader from "@/components/dashboard/DashboardHeader";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { usePendingCount } from "@/hooks/usePendingSubmissions";
import { cn } from "@/lib/utils";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { useOrganization } from "@/contexts/OrganizationContext";
import { SuperAdminSwitcher } from "@/components/SuperAdminSwitcher";
import { Sheet, SheetContent } from "@/components/ui/sheet";

const AdminLayout: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { organization, isSuperAdmin, isLoading: orgLoading } = useOrganization();
  const [isAdmin, setIsAdmin] = useState(false);
  const [isModerator, setIsModerator] = useState(false);
  const [loading, setLoading] = useState(true);
  const { count: pendingCount } = usePendingCount();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Collapsible state for settings
  const [settingsOpen, setSettingsOpen] = useState(
    location.pathname.startsWith("/admin/settings")
  );

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  const isActive = (path: string) =>
    location.pathname === path || 
    (path !== "/admin" && location.pathname.startsWith(path));

  const linkClass = (path: string) =>
    cn(
      "flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors",
      isActive(path) 
        ? "bg-primary/10 text-primary" 
        : "text-muted-foreground hover:bg-muted"
    );

  useEffect(() => {
    const checkUserRoles = async () => {
      try {
        const { data: { user }, error: userError } = await supabase.auth.getUser();
        
        if (userError) {
          console.warn('AdminLayout: Error getting user:', userError.message);
          setIsAdmin(false);
          setIsModerator(false);
          setLoading(false);
          return;
        }
        
        if (user) {
          const { data: roles, error: rolesError } = await supabase
            .from("user_roles")
            .select("role")
            .eq("user_id", user.id);
          
          if (rolesError) {
            console.warn('AdminLayout: Error checking roles:', rolesError.message);
            setIsAdmin(false);
            setIsModerator(false);
          } else {
            const roleList = roles?.map(r => r.role) ?? [];
            setIsAdmin(roleList.includes('admin'));
            setIsModerator(roleList.includes('moderator') && !roleList.includes('admin') && !roleList.includes('editor'));
          }
        } else {
          setIsAdmin(false);
          setIsModerator(false);
        }
      } catch (e) {
        console.warn('AdminLayout: Unexpected error during role check:', e);
        setIsAdmin(false);
        setIsModerator(false);
      } finally {
        setLoading(false);
      }
    };

    checkUserRoles();

    // Loading timeout safety - force loading to false after 10 seconds
    const timeout = setTimeout(() => {
      setLoading(prevLoading => {
        if (prevLoading) {
          console.warn('AdminLayout: Loading timeout reached, forcing completion');
          return false;
        }
        return prevLoading;
      });
    }, 10000);

    return () => clearTimeout(timeout);
  }, []);

  const handleLogout = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) {
      toast.error("Failed to log out");
    } else {
      toast.success("Logged out successfully");
      navigate("/login");
    }
  };

  // Wait for organization context to finish loading before rendering layout
  if (orgLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }

  // Extracted sidebar content for reuse in desktop and mobile
  const SidebarContent = () => (
    <>
      <div className="h-16 flex items-center justify-between px-6 border-b border-border">
        <span className="text-lg font-bold font-display truncate">
          {organization?.name ?? 'Admin'}
        </span>
      </div>
      <nav className="flex-grow p-4 space-y-1 text-sm font-medium overflow-y-auto">
        {/* Overview */}
        <Link to="/admin" className={linkClass("/admin") + (location.pathname !== "/admin" ? " text-muted-foreground hover:bg-muted" : "")}>
          <LayoutDashboard size={18} /> Overview
        </Link>

        {/* Events Hub - only if enabled */}
        {organization?.settings?.enable_events !== false && (
          <Link to="/admin/events" className={linkClass("/admin/events")}>
            <Calendar size={18} /> Events
          </Link>
        )}

        {/* Audience Hub */}
        <Link to="/admin/audience" className={linkClass("/admin/audience")}>
          <Users size={18} /> Audience
        </Link>

        {/* Content Hub (Library + Pages) */}
        <Link to="/admin/content" className={cn(linkClass("/admin/content"), "justify-between")}>
          <span className="flex items-center gap-3">
            <BookOpen size={18} /> Content
          </span>
          {pendingCount > 0 && (
            <Badge className="h-5 min-w-[20px] px-1.5 bg-amber-500 text-amber-950 hover:bg-amber-500 text-xs">
              {pendingCount}
            </Badge>
          )}
        </Link>

        {/* Analytics Hub */}
        <Link to="/admin/analytics" className={linkClass("/admin/analytics")}>
          <BarChart3 size={18} /> Analytics
        </Link>

        {/* Settings - Collapsible (Admin only, hidden from moderators) */}
        {isAdmin && !isModerator && (
          <Collapsible open={settingsOpen} onOpenChange={setSettingsOpen}>
            <CollapsibleTrigger className="flex items-center justify-between w-full px-3 py-2 rounded-md text-muted-foreground hover:bg-muted transition-colors">
              <span className="flex items-center gap-3">
                <Settings size={18} /> Settings
              </span>
              {settingsOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            </CollapsibleTrigger>
            <CollapsibleContent className="ml-6 space-y-1 mt-1">
              <Link to="/admin/settings/users" className={linkClass("/admin/settings/users")}>
                <Users size={16} /> Users
              </Link>
              <Link to="/admin/settings/email-templates" className={linkClass("/admin/settings/email-templates")}>
                <Mail size={16} /> Email Templates
              </Link>
              {organization?.settings?.enable_integrations && (
                <Link to="/admin/settings/integrations" className={linkClass("/admin/settings/integrations")}>
                  <Plug size={16} /> Integrations
                </Link>
              )}
            </CollapsibleContent>
          </Collapsible>
        )}
      </nav>
      <div className="p-4 border-t border-border space-y-1">
        {isSuperAdmin && (
          <Link to="/super-admin" className="flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium text-muted-foreground hover:bg-muted">
            <Shield size={18} /> Super Admin
          </Link>
        )}
        <Link to="/dashboard" className="flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium text-muted-foreground hover:bg-muted">
          <User size={18} /> My Dashboard
        </Link>
        <Link to="/" className="flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium text-muted-foreground hover:bg-muted">
          <Home size={18} /> Return to Site
        </Link>
        <button
          onClick={handleLogout}
          className="w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
        >
          <LogOut size={18} /> Log Out
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen flex flex-col">
      {isSuperAdmin && <SuperAdminSwitcher />}
      <div className="flex-grow flex">
        {/* Desktop Sidebar - hidden on mobile/tablet */}
        <div className="hidden lg:flex w-64 bg-card border-r border-border flex-col">
          <SidebarContent />
        </div>

        {/* Mobile Sheet Sidebar */}
        <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
          <SheetContent side="left" className="w-72 p-0 bg-card flex flex-col">
            <SidebarContent />
          </SheetContent>
        </Sheet>

        {/* Main content area - full width on mobile */}
        <div className="flex-grow flex flex-col overflow-hidden bg-muted/30">
          <DashboardHeader 
            showMenuButton={true}
            onMenuClick={() => setMobileMenuOpen(true)}
          />
          <div className="flex-grow overflow-auto">
            <Outlet />
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdminLayout;
